import { InventoryUserEntry } from './inventory';

/**
 * TypeScript type definitions for Airflow API integration
 */

/**
 * Pipeline execution modes for productivity data processing
 *
 * INSIGHTS_ONLY mode is intentionally unavailable in the managed UI/API as of
 * 2025-09-22 (see tasks/prd-remove-insights-only-ui-api.md for rationale). The
 * backend continues to support only the reliable modes:
 * - FULL: Complete ETL processing followed by insights generation
 * - ETL_ONLY: Data processing only, skipping insights generation
 */
export enum PipelineMode {
  FULL = 'full',        // ETL + Insights processing
  ETL_ONLY = 'etl_only' // ETL processing only, skip insights
}

export interface TriggerRequest {
  date: string;
  mode: PipelineMode;
  filter_users: boolean;
  user_id?: string;
  dag_id?: string;
}

export interface TriggerResponse {
  dag_id: string;
  dag_run_id: string;
  logical_date: string;
  state: string;
  message: string;
}

export interface PipelineStatus {
  dag_id: string;
  dag_run_id: string;
  state: string;
  start_date?: string;
  end_date?: string;
  logical_date: string;
  duration?: number;
}

export interface DAGRun {
  dag_id: string;
  dag_run_id: string;
  state: string;
  execution_date: string;
  start_date?: string;
  end_date?: string;
  logical_date: string;
}

export interface DAGRunsList {
  dag_runs: DAGRun[];
  total_entries?: number;
}

export interface BatchTriggerRequest {
  start_date: string;
  end_date: string;
  mode: PipelineMode;
  filter_users: boolean;
  delay_seconds?: number;
}

export interface BatchTriggerResponse {
  message: string;
  total_dates: number;
  successful_triggers: number;
  failed_triggers: number;
  results: Array<{
    date: string;
    status: 'triggered' | 'failed';
    dag_run_id?: string;
    error?: string;
  }>;
}

export interface HealthStatus {
  status: 'healthy' | 'unhealthy' | 'not_configured';
  airflow_connected: boolean;
  base_url?: string;
  default_dag?: string;
  message?: string;
  error?: string;
  timestamp: string;
}

export interface TriggerAndWaitResponse {
  dag_id: string;
  dag_run_id: string;
  final_state: string;
  message: string;
  date: string;
  mode: string;
  filter_users: boolean;
}

// Pipeline state enums
export enum PipelineState {
  QUEUED = 'queued',
  RUNNING = 'running',
  SUCCESS = 'success',
  FAILED = 'failed',
  SKIPPED = 'skipped',
  UP_FOR_RETRY = 'up_for_retry',
  UP_FOR_RESCHEDULE = 'up_for_reschedule',
  UPSTREAM_FAILED = 'upstream_failed',
  RESTARTING = 'restarting'
}

// UI-specific interfaces
export interface PipelineRunHistory {
  dagId: string;
  runId: string;
  date: string;
  state: PipelineState;
  startTime?: string;
  endTime?: string;
  duration?: number;
  mode?: PipelineMode;
  userFiltered?: boolean;
}

export interface NotificationMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: Date;
  autoHide?: boolean;
  duration?: number;
}

// Error handling
export interface AirflowApiError {
  status: number;
  message: string;
  detail?: string;
  code?: string;
}

// Component prop interfaces
export interface AirflowPipelineModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: string;
  selectedUserId: string;
  onPipelineTriggered?: (date: string, runId: string) => void;
  availableUsers?: InventoryUserEntry[];
}

export interface PipelineStatusProps {
  dagId: string;
  runId: string;
  onStatusChange?: (status: PipelineState) => void;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export interface NotificationToastProps {
  notification: NotificationMessage;
  onDismiss: (id: string) => void;
}

export interface PipelineHistoryProps {
  selectedDate?: string;
  limit?: number;
  showFilters?: boolean;
  onRunSelect?: (run: PipelineRunHistory) => void;
}

// Utility type for pipeline mode display
export interface PipelineModeOption {
  value: PipelineMode;
  label: string;
  description: string;
}

export const PIPELINE_MODE_OPTIONS: PipelineModeOption[] = [
  {
    value: PipelineMode.FULL,
    label: 'Complete Process (Data & Insights)',
    description: 'Process data and generate insights automatically'
  },
  {
    value: PipelineMode.ETL_ONLY,
    label: 'Process Data Only',
    description: 'Prepare and organize raw data for future insights'
  }
];

// State color mapping for UI
export const PIPELINE_STATE_COLORS: Record<PipelineState, string> = {
  [PipelineState.QUEUED]: '#6b7280',
  [PipelineState.RUNNING]: '#3b82f6',
  [PipelineState.SUCCESS]: '#10b981',
  [PipelineState.FAILED]: '#ef4444',
  [PipelineState.SKIPPED]: '#f59e0b',
  [PipelineState.UP_FOR_RETRY]: '#f97316',
  [PipelineState.UP_FOR_RESCHEDULE]: '#8b5cf6',
  [PipelineState.UPSTREAM_FAILED]: '#ef4444',
  [PipelineState.RESTARTING]: '#06b6d4'
};

// State display labels
export const PIPELINE_STATE_LABELS: Record<PipelineState, string> = {
  [PipelineState.QUEUED]: 'Queued',
  [PipelineState.RUNNING]: 'Running',
  [PipelineState.SUCCESS]: 'Success',
  [PipelineState.FAILED]: 'Failed',
  [PipelineState.SKIPPED]: 'Skipped',
  [PipelineState.UP_FOR_RETRY]: 'Retrying',
  [PipelineState.UP_FOR_RESCHEDULE]: 'Rescheduled',
  [PipelineState.UPSTREAM_FAILED]: 'Upstream Failed',
  [PipelineState.RESTARTING]: 'Restarting'
};

// New types for complete pipeline monitoring (dual-DAG)
export interface PipelinePhase {
  dag_id: string;
  dag_run_id?: string;
  state: string;
  start_date?: string;
  end_date?: string;
  duration?: number;
}

export interface CompletePipelineStatus {
  date: string;
  pipeline_mode: string;
  etl_phase: PipelinePhase;
  insights_phase?: PipelinePhase;
  overall_state: string; // queued, running, success, failed, partial_success, waiting
  overall_progress: string;
  started_at?: string;
  completed_at?: string;
  total_duration?: number;
}

// Component props for complete pipeline monitoring
export interface CompletePipelineStatusProps {
  date: string;
  onStatusChange?: (status: CompletePipelineStatus) => void;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

// Overall pipeline states (different from individual DAG states)
export enum OverallPipelineState {
  QUEUED = 'queued',
  RUNNING = 'running',
  WAITING = 'waiting',
  SUCCESS = 'success',
  FAILED = 'failed',
  PARTIAL_SUCCESS = 'partial_success',
  UNKNOWN = 'unknown'
}

// Colors for overall pipeline states
export const OVERALL_PIPELINE_STATE_COLORS: Record<OverallPipelineState, string> = {
  [OverallPipelineState.QUEUED]: '#6b7280',
  [OverallPipelineState.RUNNING]: '#3b82f6',
  [OverallPipelineState.WAITING]: '#8b5cf6',
  [OverallPipelineState.SUCCESS]: '#10b981',
  [OverallPipelineState.FAILED]: '#ef4444',
  [OverallPipelineState.PARTIAL_SUCCESS]: '#f59e0b',
  [OverallPipelineState.UNKNOWN]: '#6b7280'
};
